import { AxiosResponse } from 'axios';
import queryString from 'query-string';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { createRepository } from '@/repositories/GenericRepository';
import {
  AutoReverifyPayload,
  BulkAutoReverifyPayload,
  CheckVOB,
  CreateInsuranceFromDiscoveredRequest,
  CreateInsuranceVerificationRequest,
  ExternalVerification,
  ExternalVerificationSource
} from '@/types/externalVerification';
import { InsurancePolicy } from '@/types/insurancePolicy';
import {
  InsuranceVerification,
  InsuranceVerificationListItem
} from '@/types/insuranceVerification';
import PaginatedResponse from '@/types/paginatedResponse';
import ajax from '@/utils/ajax';

const basePath = '/api/dugout/verifications';

const serializeRepeatedParams = (params: Record<string, unknown>): string =>
  queryString.stringify(params, { arrayFormat: 'none' });

export const externalVerificationRepository = {
  ...createRepository<ExternalVerification, ExternalVerification>(basePath),

  async listInsuranceVerifications({
    host,
    page = '1',
    size,
    ordering,
    filters
  }: {
    host: string;
    page?: string;
    size?: string;
    ordering?: string;
    filters?: Record<string, string | string[]>;
  }): Promise<PaginatedResponse<InsuranceVerificationListItem[]>> {
    const response = await ajax.get({
      url: `${host}${basePath}/vob/list/`,
      params: { ...filters, page, ...(size && { size }), ...(ordering && { ordering }) },
      config: { paramsSerializer: { serialize: serializeRepeatedParams } }
    });

    return response.data;
  },

  async listInsuranceVerificationFilterOptions({
    host
  }: {
    host: string;
  }): Promise<{ carriers: string[]; plans: string[] }> {
    const response = await ajax.get({ url: `${host}${basePath}/vob/list/filter-options/` });

    return response.data;
  },

  async insuranceVOB({
    host,
    policyId,
    data
  }: {
    host: string;
    policyId: string;
    data?: CheckVOB;
  }): Promise<ExternalVerification> {
    const response = await ajax.post<CheckVOB, AxiosResponse<ExternalVerification>>({
      url: `${host}${basePath}/vob/${policyId}/`,
      data: data ?? {}
    });

    return response.data;
  },

  async createInsuranceVerification({
    host,
    data
  }: {
    host: string;
    data: CreateInsuranceVerificationRequest;
  }): Promise<InsuranceVerification> {
    const response = await ajax.post<
      CreateInsuranceVerificationRequest,
      AxiosResponse<InsuranceVerification>
    >({
      url: `${host}${basePath}/vob/create-insurance-verification/`,
      data
    });

    return response.data;
  },

  async createInsuranceFromDiscoveredPolicy({
    host,
    data
  }: {
    host: string;
    data: CreateInsuranceFromDiscoveredRequest;
  }): Promise<InsurancePolicy> {
    const response = await ajax.post<
      CreateInsuranceFromDiscoveredRequest,
      AxiosResponse<InsurancePolicy>
    >({
      url: `${host}${basePath}/vob/create-insurance-policy/`,
      data
    });

    return response.data;
  },

  async autoReverifyPolicy({
    host,
    policyId,
    data
  }: {
    host: string;
    policyId: string;
    data: AutoReverifyPayload;
  }): Promise<InsuranceVerification> {
    const response = await ajax.post<AutoReverifyPayload, AxiosResponse<InsuranceVerification>>({
      url: `${host}${basePath}/vob/auto-reverify/${policyId}/`,
      data: data
    });

    return response.data;
  },

  async bulkAutoReverifyPolicies({
    host,
    data
  }: {
    host: string;
    data: BulkAutoReverifyPayload;
  }): Promise<{ detail: string }> {
    const response = await ajax.post<BulkAutoReverifyPayload, AxiosResponse<{ detail: string }>>({
      url: `${host}${basePath}/vob/auto-bulk-reverify/`,
      data
    });

    return response.data;
  }
};

const ExternalVerificationRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async listInsuranceVerifications({
      page,
      size,
      ordering,
      filters
    }: {
      page?: string;
      size?: string;
      ordering?: string;
      filters?: Record<string, string | string[]>;
    }) {
      return externalVerificationRepository.listInsuranceVerifications({
        host,
        page,
        size,
        ordering,
        filters
      });
    },
    async listInsuranceVerificationFilterOptions() {
      return externalVerificationRepository.listInsuranceVerificationFilterOptions({ host });
    },
    async insuranceVOB({ policyId, data }: { policyId: string; data?: CheckVOB }) {
      return externalVerificationRepository.insuranceVOB({ host, policyId, data });
    },
    async createInsuranceVerification(data: CreateInsuranceVerificationRequest) {
      return externalVerificationRepository.createInsuranceVerification({ host, data });
    },
    async createInsuranceFromDiscoveredPolicy(data: CreateInsuranceFromDiscoveredRequest) {
      return externalVerificationRepository.createInsuranceFromDiscoveredPolicy({ host, data });
    },
    async autoReverifyPolicy({ policyId, data }: { policyId: string; data: AutoReverifyPayload }) {
      return externalVerificationRepository.autoReverifyPolicy({ host, policyId, data });
    },
    async bulkAutoReverifyPolicies(policy_ids: string[], vendor?: ExternalVerificationSource) {
      return externalVerificationRepository.bulkAutoReverifyPolicies({
        host,
        data: { policy_ids, ...(vendor ? { vendor } : {}) }
      });
    }
  };
};

export default ExternalVerificationRepository;
